/**
 * MothershipBrain - The Central AI Command Interface
 *
 * A floating, centered command hub that looks like you're on the bridge
 * of a spaceship. Voice-first, with real-time status, typing animation,
 * and that "AI is actually running everything" vibe.
 *
 * Features:
 * - Mini mode: Small floating orb you can click to expand
 * - Full mode: Centered command hub with all controls
 * - Chat history stored to Supabase
 * - Admin mode with full access, client mode sandboxed
 */
import { useState, useRef, useEffect, useCallback } from 'react';
import {
  Mic, MicOff, X, Send, Loader2, Volume2, VolumeX,
  Activity, Zap, Sparkles, Minimize2, Maximize2, Crosshair, Trash2, Shield, User
} from 'lucide-react';
import { supabase } from '../lib/supabase';

const SYNAPSE_API = '/api/synapse';

// Thinking thoughts - cycle through these while processing
const THINKING_THOUGHTS = [
  'Analyzing request...',
  'Consulting the hivemind...',
  'Processing neural pathways...',
  'Synthesizing response...',
  'Checking knowledge banks...',
  'Running inference...',
  'Computing optimal path...',
  'Querying systems...',
  'Formulating thoughts...',
  'Almost there...'
];

// SECURITY: Admin mode default is determined by dashboard context
// Can be toggled for testing via UI
const getDefaultAdminMode = () =>
  window.location.pathname.startsWith('/dashboard') ||
  window.location.hostname === 'localhost' ||
  window.location.hostname.includes('dev.');

interface BrainState {
  mode: 'idle' | 'listening' | 'thinking' | 'speaking' | 'processing';
  transcript: string;
  response: string;
  displayedResponse: string;
  audioLevel: number;
  systemStatus: {
    synapse: boolean;
    workers: number;
    queue: number;
    tasks_completed: number;
    cpu_percent: number;
    memory_mb: number;
  };
}

interface MothershipBrainProps {
  isOpen: boolean;
  onClose: () => void;
  startInMiniMode?: boolean;
  startInPopupMode?: boolean;
}

// Get or create persistent session ID
function getSessionId(): string {
  const key = 'mothership_brain_session';
  let id = localStorage.getItem(key);
  if (!id) {
    id = `brain_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem(key, id);
  }
  return id;
}

// Store chat to Supabase
async function storeChatMessage(role: 'user' | 'ai', content: string, sessionId: string, isAdmin: boolean) {
  try {
    await supabase.from('brain_chats').insert({
      session_id: sessionId,
      role,
      content,
      is_admin: isAdmin,
      created_at: new Date().toISOString()
    });
  } catch (e) {
    // Silent fail - chat storage is non-critical
    console.warn('Failed to store chat:', e);
  }
}

// Load chat history from Supabase
async function loadChatHistory(sessionId: string): Promise<Array<{ role: 'user' | 'ai'; text: string }>> {
  try {
    const { data, error } = await supabase
      .from('brain_chats')
      .select('role, content')
      .eq('session_id', sessionId)
      .order('created_at', { ascending: true })
      .limit(50);

    if (error) throw error;
    return (data || []).map(m => ({ role: m.role as 'user' | 'ai', text: m.content }));
  } catch (e) {
    console.warn('Failed to load chat history:', e);
    return [];
  }
}

export default function MothershipBrain({ isOpen, onClose, startInMiniMode = false, startInPopupMode = true }: MothershipBrainProps) {
  const [state, setState] = useState<BrainState>({
    mode: 'idle',
    transcript: '',
    response: '',
    displayedResponse: '',
    audioLevel: 0,
    systemStatus: { synapse: false, workers: 0, queue: 0, tasks_completed: 0, cpu_percent: 0, memory_mb: 0 }
  });

  const [textInput, setTextInput] = useState('');
  const [isMuted, setIsMuted] = useState(false);
  const [history, setHistory] = useState<Array<{ role: 'user' | 'ai'; text: string }>>([]);
  const [miniMode, setMiniMode] = useState(startInMiniMode);
  const [sessionId] = useState(getSessionId);
  const [popupMode, setPopupMode] = useState(startInPopupMode); // Bottom-right popup mode (default)
  const [thinkingThought, setThinkingThought] = useState(THINKING_THOUGHTS[0]);
  const [showChecklist, setShowChecklist] = useState(false);
  const [tasks, setTasks] = useState<Array<{ id: string; text: string; done: boolean }>>([]);
  const [newTaskText, setNewTaskText] = useState('');
  const [taskPromptMode, setTaskPromptMode] = useState(false); // When user clicked Submit Task but needs description
  const [wasVoiceInput, setWasVoiceInput] = useState(false); // Track if last input was voice
  const [isAdminMode, setIsAdminMode] = useState(getDefaultAdminMode); // Toggleable admin/client mode
  const [subAgentCount, setSubAgentCount] = useState(1); // Sub-agents: 1-3

  // Inspector state - select elements for context
  const [inspectMode, setInspectMode] = useState(false);
  const [selectedElements, setSelectedElements] = useState<Array<{
    id: string;
    component: string;
    file?: string;
    tag: string;
    classes: string;
    text: string;
  }>>([]);

  const recognitionRef = useRef<any>(null);
  const synthRef = useRef<SpeechSynthesisUtterance | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationRef = useRef<number>(0);
  const typewriterRef = useRef<number>(0);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const textInputRef = useRef<HTMLInputElement>(null);

  // Escape key to close - also reset inspect mode
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        setInspectMode(false); // Clean up inspector
        onClose();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  // Clean up inspect mode when panel closes
  useEffect(() => {
    if (!isOpen) {
      setInspectMode(false);
    }
  }, [isOpen]);

  // Load chat history on mount
  useEffect(() => {
    loadChatHistory(sessionId).then(h => {
      if (h.length > 0) setHistory(h);
    });
  }, [sessionId]);

  // Fetch system status periodically
  useEffect(() => {
    if (!isOpen) return;

    const fetchStatus = async () => {
      try {
        const [statusRes, metricsRes] = await Promise.all([
          fetch(`${SYNAPSE_API}/status`, { headers: { 'X-API-Key': 'synapse-dev' } }),
          fetch(`${SYNAPSE_API}/metrics`, { headers: { 'X-API-Key': 'synapse-dev' } })
        ]);

        const status = statusRes.ok ? await statusRes.json() : {};
        const metrics = metricsRes.ok ? await metricsRes.json() : {};

        setState(prev => ({
          ...prev,
          systemStatus: {
            synapse: status.status === 'running' || status.healthy,
            workers: metrics.active_tasks ?? status.workers?.length ?? 0,
            queue: metrics.queue_depth ?? status.queue_size ?? 0,
            tasks_completed: metrics.tasks_completed ?? 0,
            cpu_percent: metrics.cpu_percent ?? 0,
            memory_mb: metrics.memory_mb ?? 0
          }
        }));
      } catch (e) {
        // Silent fail
      }
    };

    fetchStatus();
    const interval = setInterval(fetchStatus, 5000);
    return () => clearInterval(interval);
  }, [isOpen]);

  // Typewriter effect for responses
  useEffect(() => {
    if (state.response && state.response !== state.displayedResponse) {
      let i = state.displayedResponse.length;

      const typeNext = () => {
        if (i < state.response.length) {
          setState(prev => ({
            ...prev,
            displayedResponse: state.response.slice(0, i + 1)
          }));
          i++;
          typewriterRef.current = window.setTimeout(typeNext, 15 + Math.random() * 25);
        } else {
          setState(prev => ({ ...prev, mode: 'idle' }));
        }
      };

      typewriterRef.current = window.setTimeout(typeNext, 50);
      return () => clearTimeout(typewriterRef.current);
    }
  }, [state.response]);

  // Cycle through thinking thoughts while processing
  useEffect(() => {
    if (state.mode === 'thinking') {
      let idx = 0;
      setThinkingThought(THINKING_THOUGHTS[0]);
      const interval = setInterval(() => {
        idx = (idx + 1) % THINKING_THOUGHTS.length;
        setThinkingThought(THINKING_THOUGHTS[idx]);
      }, 1500); // Change thought every 1.5 seconds
      return () => clearInterval(interval);
    }
  }, [state.mode]);

  // Auto-scroll chat to bottom when new messages
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [history, state.displayedResponse]);

  // Inspector mode - capture element clicks
  useEffect(() => {
    if (!inspectMode) return;

    const handleInspectClick = (e: MouseEvent) => {
      // Don't capture clicks on the panel itself
      const target = e.target as HTMLElement;
      if (target.closest('.mothership-brain-panel')) return;

      e.preventDefault();
      e.stopPropagation();

      // Get element info
      const component = target.getAttribute('data-component') || target.closest('[data-component]')?.getAttribute('data-component') || 'Unknown';
      const file = target.getAttribute('data-file') || target.closest('[data-file]')?.getAttribute('data-file') || undefined;
      const tag = target.tagName.toLowerCase();
      const classes = target.className?.toString() || '';
      const text = target.textContent?.slice(0, 50) || '';
      const id = `${component}-${Date.now()}`;

      // Add to selected elements if not already there
      setSelectedElements(prev => {
        // Check if similar element exists
        const exists = prev.some(el => el.component === component && el.text === text);
        if (exists) return prev;
        return [...prev, { id, component, file, tag, classes, text }];
      });
    };

    // Add highlight on hover
    const handleInspectHover = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('.mothership-brain-panel')) return;
      target.style.outline = '2px solid #a855f7';
      target.style.outlineOffset = '2px';
    };

    const handleInspectLeave = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      target.style.outline = '';
      target.style.outlineOffset = '';
    };

    document.addEventListener('click', handleInspectClick, true);
    document.addEventListener('mouseover', handleInspectHover);
    document.addEventListener('mouseout', handleInspectLeave);

    return () => {
      document.removeEventListener('click', handleInspectClick, true);
      document.removeEventListener('mouseover', handleInspectHover);
      document.removeEventListener('mouseout', handleInspectLeave);
      // Clean up any lingering outlines
      document.querySelectorAll('[style*="outline"]').forEach(el => {
        (el as HTMLElement).style.outline = '';
        (el as HTMLElement).style.outlineOffset = '';
      });
    };
  }, [inspectMode]);

  // Audio level visualization
  const startAudioAnalysis = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioContextRef.current = new AudioContext();
      analyserRef.current = audioContextRef.current.createAnalyser();
      const source = audioContextRef.current.createMediaStreamSource(stream);
      source.connect(analyserRef.current);
      analyserRef.current.fftSize = 256;

      const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);

      const analyze = () => {
        if (!analyserRef.current) return;
        analyserRef.current.getByteFrequencyData(dataArray);
        const avg = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
        setState(prev => ({ ...prev, audioLevel: avg / 255 }));
        animationRef.current = requestAnimationFrame(analyze);
      };

      analyze();
    } catch (e) {
      console.error('Audio analysis failed:', e);
    }
  }, []);

  const stopAudioAnalysis = useCallback(() => {
    if (animationRef.current) cancelAnimationFrame(animationRef.current);
    if (audioContextRef.current) audioContextRef.current.close();
    audioContextRef.current = null;
    analyserRef.current = null;
    setState(prev => ({ ...prev, audioLevel: 0 }));
  }, []);

  // Speech recognition
  // Simple speech recognition - no auto-restart, no complex state tracking
  const startListening = useCallback(() => {
    if (!('webkitSpeechRecognition' in window || 'SpeechRecognition' in window)) {
      console.error('Speech recognition not supported');
      return;
    }

    // Stop any existing instance
    if (recognitionRef.current) {
      try { recognitionRef.current.abort(); } catch {}
      recognitionRef.current = null;
    }

    const SpeechRecognition = (window as any).webkitSpeechRecognition || (window as any).SpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.continuous = true; // Keep listening until manually stopped
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    recognition.maxAlternatives = 1;

    // Track finalized segments by index to prevent duplicate processing
    let finalTranscript = '';
    const processedFinalIndices = new Set<number>();

    recognition.onresult = (event: any) => {
      let interim = '';

      for (let i = 0; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          // Only process each final result ONCE (Chrome can fire duplicates)
          if (!processedFinalIndices.has(i)) {
            processedFinalIndices.add(i);
            finalTranscript += result[0].transcript;
          }
        } else {
          // Latest interim only (replaces, doesn't append)
          interim = result[0].transcript;
        }
      }

      // Display: all finals + current interim
      setState(prev => ({
        ...prev,
        transcript: finalTranscript + interim
      }));
    };

    recognition.onerror = (event: any) => {
      console.error('Speech recognition error:', event.error);
      if (event.error !== 'no-speech') {
        setState(prev => ({ ...prev, mode: 'idle' }));
      }
    };

    recognition.onend = () => {
      // Don't auto-restart - let it end naturally when user stops speaking
      // User can click the button again if they want to continue
    };

    recognitionRef.current = recognition;
    recognition.start();
    startAudioAnalysis();
    setState(prev => ({ ...prev, mode: 'listening', transcript: '' }));
  }, [startAudioAnalysis]);

  const stopListening = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    stopAudioAnalysis();

    if (state.transcript.trim()) {
      setWasVoiceInput(true);
      processInput(state.transcript.trim());
    } else {
      setState(prev => ({ ...prev, mode: 'idle' }));
    }
  }, [state.transcript, stopAudioAnalysis]);

  // Process input (voice or text)
  const processInput = async (input: string) => {
    setState(prev => ({
      ...prev,
      mode: 'thinking',
      response: '',
      displayedResponse: ''
    }));

    setHistory(prev => [...prev, { role: 'user', text: input }]);

    try {
      // Check for system commands first
      const lowerInput = input.toLowerCase();

      if (lowerInput.includes('status') || lowerInput.includes('how are you')) {
        const response = `Systems operational. ${state.systemStatus.workers} workers active, ${state.systemStatus.queue} tasks queued, ${state.systemStatus.tasks_completed} completed. All systems nominal.`;
        handleResponse(response);
        return;
      }

      if (lowerInput.includes('submit') && lowerInput.includes('task')) {
        // Submit to pipeline - extract task description
        const taskDesc = input.replace(/submit|task|please|to pipeline|a /gi, '').trim();

        // If no description provided, ask for it
        if (!taskDesc || taskDesc.length < 3) {
          handleResponse("What would you like me to work on? Say 'submit task' followed by your request.");
          return;
        }

        const res = await fetch(`${SYNAPSE_API}/tasks`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-API-Key': 'synapse-dev' },
          body: JSON.stringify({ description: taskDesc, priority: "5", approve_opus: true })
        });

        if (res.ok) {
          const data = await res.json();
          handleResponse(`Task submitted: "${taskDesc.slice(0, 50)}${taskDesc.length > 50 ? '...' : ''}". Task ID: ${data.task_id?.slice(0, 8)}. Working on it now.`);
        } else {
          handleResponse('Failed to submit task. Pipeline may be offline.');
        }
        return;
      }

      // Default: send to Claude with is_admin flag
      // SECURITY: is_admin determines what prompt/access level is used
      const elementContext = selectedElements.length > 0
        ? `\n\nSELECTED UI ELEMENTS (user is referring to these):\n${selectedElements.map(el =>
            `- ${el.component}${el.file ? ` (${el.file})` : ''}: <${el.tag}> "${el.text.slice(0, 30)}..."`
          ).join('\n')}`
        : '';

      const systemContext = `You are Mothership AI, a voice assistant for the Mothership platform.
RULES:
- Always respond in English
- Keep responses SHORT and conversational (1-3 sentences max)
- NEVER include code in your responses - you are a voice interface
- If asked to do something technical, acknowledge and say you'll submit it to the pipeline
- For task requests, respond with a brief summary of what you'll do
- Be friendly, concise, and direct${elementContext}`;

      const res = await fetch(`${SYNAPSE_API}/claude/quick`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-API-Key': 'synapse-dev' },
        body: JSON.stringify({
          message: `${systemContext}\n\nUser: ${input}`,
          history: history.slice(-6).map(h => ({ role: h.role === 'ai' ? 'assistant' : 'user', content: h.text })),
          is_admin: isAdminMode
        })
      });

      // Store user message to DB
      storeChatMessage('user', input, sessionId, isAdminMode);

      if (res.ok) {
        const data = await res.json();
        const response = data.response || 'No response received.';
        // Store AI response to DB
        storeChatMessage('ai', response, sessionId, isAdminMode);
        handleResponse(response);
      } else {
        handleResponse('Unable to connect to AI systems. Check Synapse status.');
      }
    } catch (e) {
      handleResponse('Communication error. Systems may be offline.');
    }
  };

  const handleResponse = (text: string) => {
    setState(prev => ({
      ...prev,
      mode: 'speaking',
      response: text,
      transcript: ''
    }));

    setHistory(prev => [...prev, { role: 'ai', text }]);

    // Only speak if it was voice input and not muted
    if (wasVoiceInput && !isMuted) {
      speakWithOpenAI(text);
    }

    // Reset voice input flag after handling
    setWasVoiceInput(false);
  };

  // OpenAI TTS - HD quality voice
  const speakWithOpenAI = async (text: string) => {
    try {
      const response = await fetch(`${SYNAPSE_API}/voice/tts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': 'synapse-dev'
        },
        body: JSON.stringify({
          text: text,
          voice: 'nova',  // Options: alloy, echo, fable, onyx, nova, shimmer
          model: 'tts-1'  // Standard (half price of HD)
        })
      });

      if (response.ok) {
        const audioBlob = await response.blob();
        const audioUrl = URL.createObjectURL(audioBlob);
        const audio = new Audio(audioUrl);
        audio.onended = () => URL.revokeObjectURL(audioUrl);
        await audio.play();
      } else {
        // Fallback to browser speech if TTS fails
        console.warn('OpenAI TTS failed, falling back to browser speech');
        if ('speechSynthesis' in window) {
          const utterance = new SpeechSynthesisUtterance(text);
          utterance.rate = 1.1;
          utterance.lang = 'en-US';
          window.speechSynthesis.speak(utterance);
        }
      }
    } catch (e) {
      console.error('TTS error:', e);
      // Fallback
      if ('speechSynthesis' in window) {
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.rate = 1.1;
        window.speechSynthesis.speak(utterance);
      }
    }
  };

  const handleTextSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!textInput.trim()) return;

    // If in task prompt mode, submit directly to pipeline
    if (taskPromptMode) {
      const taskDesc = textInput.trim();
      setTaskPromptMode(false);
      setTextInput('');

      setState(prev => ({ ...prev, mode: 'thinking' }));
      setHistory(prev => [...prev, { role: 'user', text: `📝 Task: ${taskDesc}` }]);

      try {
        const res = await fetch(`${SYNAPSE_API}/tasks`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-API-Key': 'synapse-dev' },
          body: JSON.stringify({ description: taskDesc, priority: "5", approve_opus: true })
        });

        if (res.ok) {
          const data = await res.json();
          handleResponse(`Task submitted: "${taskDesc.slice(0, 50)}${taskDesc.length > 50 ? '...' : ''}". Task ID: ${data.task_id?.slice(0, 8)}. Working on it now.`);
        } else {
          const errorText = await res.text();
          handleResponse(`Failed to submit task: ${errorText || 'Pipeline may be offline.'}`);
        }
      } catch (err) {
        handleResponse('Failed to connect to pipeline. Is Synapse running?');
      }
      return;
    }

    // Normal chat input
    processInput(textInput.trim());
    setTextInput('');
  };

  // Get mode-specific styling
  const getModeStyles = () => {
    switch (state.mode) {
      case 'listening':
        return {
          ringColor: 'from-cyan-400 via-blue-500 to-purple-500',
          glowColor: 'rgba(34, 211, 238, 0.4)',
          pulseSpeed: '1.5s'
        };
      case 'thinking':
        return {
          ringColor: 'from-purple-500 via-pink-500 to-orange-500',
          glowColor: 'rgba(168, 85, 247, 0.4)',
          pulseSpeed: '0.8s'
        };
      case 'speaking':
        return {
          ringColor: 'from-green-400 via-emerald-500 to-cyan-500',
          glowColor: 'rgba(34, 197, 94, 0.4)',
          pulseSpeed: '2s'
        };
      default:
        return {
          ringColor: 'from-gray-500 via-gray-600 to-gray-700',
          glowColor: 'rgba(100, 100, 100, 0.2)',
          pulseSpeed: '4s'
        };
    }
  };

  const modeStyles = getModeStyles();

  // Task management functions
  const addTask = (text: string) => {
    if (!text.trim()) return;
    const newTask = { id: `task_${Date.now()}`, text: text.trim(), done: false };
    setTasks(prev => [...prev, newTask]);
    setNewTaskText('');
  };

  const toggleTask = (id: string) => {
    setTasks(prev => prev.map(t => t.id === id ? { ...t, done: !t.done } : t));
  };

  const deleteTask = (id: string) => {
    setTasks(prev => prev.filter(t => t.id !== id));
  };

  if (!isOpen) return null;

  // MINI MODE: Small floating orb
  if (miniMode) {
    return (
      <div className="fixed bottom-6 right-6 z-[200]">
        {/* Mini orb button */}
        <button
          onClick={() => setMiniMode(false)}
          className="relative group"
        >
          {/* Glow effect */}
          <div
            className="absolute inset-0 rounded-full blur-lg opacity-60"
            style={{
              background: state.mode === 'listening' ? 'rgba(34,211,238,0.6)' :
                         state.mode === 'thinking' ? 'rgba(168,85,247,0.6)' :
                         state.mode === 'speaking' ? 'rgba(34,197,94,0.6)' :
                         'rgba(139,92,246,0.4)',
              animation: state.mode !== 'idle' ? 'pulse 2s ease-in-out infinite' : 'none'
            }}
          />
          {/* Main orb */}
          <div className="relative w-16 h-16 rounded-full bg-gradient-to-br from-purple-600 via-pink-500 to-cyan-400 flex items-center justify-center shadow-2xl border-2 border-white/20 group-hover:scale-110 transition-transform">
            <img src="/assets/saucer.png" alt="Brain" className="w-10 h-10 object-contain" />
          </div>
          {/* Status dot */}
          <span className={`absolute -top-1 -right-1 w-4 h-4 rounded-full border-2 border-gray-900 ${
            state.mode === 'listening' ? 'bg-cyan-400 animate-pulse' :
            state.mode === 'thinking' ? 'bg-purple-400 animate-spin' :
            state.mode === 'speaking' ? 'bg-green-400 animate-pulse' :
            state.systemStatus.synapse ? 'bg-green-500' : 'bg-red-500'
          }`} />
        </button>

        {/* Quick mute toggle */}
        <button
          onClick={(e) => { e.stopPropagation(); setIsMuted(!isMuted); }}
          className={`absolute -left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full flex items-center justify-center transition-all ${
            isMuted ? 'bg-red-500/80' : 'bg-gray-800/80'
          }`}
        >
          {isMuted ? <VolumeX className="w-4 h-4 text-white" /> : <Volume2 className="w-4 h-4 text-gray-300" />}
        </button>
      </div>
    );
  }

  // POPUP MODE: Responsive shrunken version of full mode
  if (popupMode) {
    return (
      <div className="mothership-brain-panel fixed bottom-6 right-6 z-[200] w-[380px] max-w-[calc(100vw-3rem)] max-h-[600px] flex flex-col bg-[#0d1117]/98 backdrop-blur-xl rounded-2xl border border-purple-500/30 shadow-2xl shadow-purple-500/20 overflow-hidden">
        {/* Mini Saucer Header with Shimmer */}
        <div className="relative flex flex-col items-center py-4 bg-gradient-to-b from-purple-900/30 to-transparent border-b border-purple-500/20">
          {/* Shimmer ring - scaled down */}
          <div
            className="absolute w-24 h-24 rounded-full pointer-events-none"
            style={{
              background: `conic-gradient(from 0deg, transparent 0%, rgba(139,92,246,0.4) 10%, rgba(236,72,153,0.4) 20%, rgba(34,211,238,0.4) 30%, transparent 40%, transparent 60%, rgba(34,211,238,0.4) 70%, rgba(236,72,153,0.4) 80%, rgba(139,92,246,0.4) 90%, transparent 100%)`,
              animation: 'spin 4s linear infinite',
              filter: 'blur(8px)'
            }}
          />
          {/* Saucer */}
          <div
            className="relative w-16 h-16 flex items-center justify-center transition-transform duration-100"
            style={{ transform: `scale(${1 + state.audioLevel * 0.1})` }}
          >
            <img
              src="/assets/saucer.png"
              alt="Mothership"
              className="w-14 h-auto relative z-10 drop-shadow-lg"
              style={{
                filter: `drop-shadow(0 0 10px ${modeStyles.glowColor})`,
                animation: state.mode === 'thinking' ? 'float 2s ease-in-out infinite' : 'none'
              }}
            />
            {state.mode === 'thinking' && (
              <Loader2 className="absolute w-5 h-5 text-purple-400 animate-spin" />
            )}
          </div>
          {/* Status label */}
          <div className={`mt-1 text-xs font-medium ${
            state.mode === 'listening' ? 'text-cyan-400' :
            state.mode === 'thinking' ? 'text-purple-400' :
            state.mode === 'speaking' ? 'text-green-400' :
            'text-gray-400'
          }`}>
            {state.mode === 'listening' ? 'Listening...' :
             state.mode === 'thinking' ? thinkingThought :
             state.mode === 'speaking' ? 'Speaking...' :
             'Ready'}
          </div>
          {/* Controls */}
          <div className="absolute top-2 right-2 flex gap-1">
            <button onClick={() => setPopupMode(false)} className="p-1.5 text-gray-400 hover:text-white transition-colors rounded" title="Expand">
              <Maximize2 className="w-4 h-4" />
            </button>
            <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-white transition-colors rounded">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Mini Status Bar */}
        <div className="flex items-center justify-center gap-3 px-3 py-1.5 bg-gradient-to-r from-purple-900/20 via-transparent to-cyan-900/20 border-b border-purple-500/10 text-[10px]">
          <div className={`flex items-center gap-1 ${state.systemStatus.synapse ? 'text-green-400' : 'text-red-400'}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${state.systemStatus.synapse ? 'bg-green-400' : 'bg-red-400'} animate-pulse`} />
            Synapse
          </div>
          <div className={`flex items-center gap-1 ${
            state.systemStatus.cpu_percent > 80 ? 'text-red-400' :
            state.systemStatus.cpu_percent > 50 ? 'text-yellow-400' : 'text-cyan-400'
          }`}>
            CPU {state.systemStatus.cpu_percent.toFixed(0)}%
          </div>
          <div className="text-blue-400 flex items-center gap-1">
            {(state.systemStatus.memory_mb / 1024).toFixed(1)}GB
          </div>
          <div className="text-cyan-400 flex items-center gap-1">
            <Activity className="w-3 h-3" />
            {state.systemStatus.workers}
          </div>
          <div className="text-purple-400 flex items-center gap-1">
            <Zap className="w-3 h-3" />
            {state.systemStatus.queue}
          </div>
          <div className="text-emerald-400 flex items-center gap-1">
            <Sparkles className="w-3 h-3" />
            {state.systemStatus.tasks_completed}
          </div>
          {/* Admin/Client toggle */}
          <button
            onClick={() => setIsAdminMode(!isAdminMode)}
            className={`flex items-center gap-1 px-1.5 py-0.5 rounded transition-all ${
              isAdminMode
                ? 'bg-amber-500/20 text-amber-400'
                : 'bg-blue-500/20 text-blue-400'
            }`}
            title={isAdminMode ? 'Switch to Client mode' : 'Switch to Admin mode'}
          >
            {isAdminMode ? <Shield className="w-3 h-3" /> : <User className="w-3 h-3" />}
            {isAdminMode ? 'Admin' : 'Client'}
          </button>
          {/* Sub-agent count toggle */}
          <div className="flex items-center gap-0.5 bg-purple-500/20 rounded px-1 py-0.5">
            <button
              onClick={() => setSubAgentCount(c => Math.max(1, c - 1))}
              disabled={subAgentCount <= 1}
              className="w-4 h-4 flex items-center justify-center text-purple-400 hover:text-purple-300 disabled:opacity-30 disabled:cursor-not-allowed"
              title="Remove sub-agent"
            >
              −
            </button>
            <span className="text-purple-400 font-mono text-[10px] min-w-[16px] text-center" title="Sub-agents">
              {subAgentCount}
            </span>
            <button
              onClick={() => setSubAgentCount(c => Math.min(3, c + 1))}
              disabled={subAgentCount >= 3}
              className="w-4 h-4 flex items-center justify-center text-purple-400 hover:text-purple-300 disabled:opacity-30 disabled:cursor-not-allowed"
              title="Add sub-agent"
            >
              +
            </button>
          </div>
        </div>

        {/* Inspector Panel */}
        <div className="border-b border-purple-500/10">
          {/* Inspector toggle + selected count */}
          <div className="flex items-center justify-between px-3 py-1.5 bg-gradient-to-r from-orange-900/10 to-transparent">
            <button
              onClick={() => setInspectMode(!inspectMode)}
              className={`flex items-center gap-1.5 text-xs font-medium transition-all ${
                inspectMode
                  ? 'text-orange-400 bg-orange-500/20 px-2 py-1 rounded'
                  : 'text-gray-400 hover:text-gray-300'
              }`}
            >
              <Crosshair className="w-3 h-3" />
              {inspectMode ? 'Inspecting...' : 'Inspect'}
            </button>
            {selectedElements.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-gray-500">{selectedElements.length} selected</span>
                <button
                  onClick={() => setSelectedElements([])}
                  className="text-gray-500 hover:text-red-400 transition-colors"
                  title="Clear all"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            )}
          </div>
          {/* Selected elements list */}
          {selectedElements.length > 0 && (
            <div className="px-2 py-1.5 space-y-1 max-h-[80px] overflow-y-auto bg-black/20">
              {selectedElements.map((el) => (
                <div key={el.id} className="flex items-center justify-between text-[10px] bg-gray-800/50 rounded px-2 py-1">
                  <div className="flex items-center gap-1.5 overflow-hidden">
                    <span className="text-orange-400 font-mono">{el.component}</span>
                    {el.file && <span className="text-gray-500 truncate">({el.file.split('/').pop()})</span>}
                  </div>
                  <button
                    onClick={() => setSelectedElements(prev => prev.filter(e => e.id !== el.id))}
                    className="text-gray-500 hover:text-red-400 flex-shrink-0"
                  >
                    <X className="w-2.5 h-2.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Chat history */}
        <div ref={chatContainerRef} className="flex-1 overflow-y-auto p-3 space-y-2 min-h-[150px] max-h-[250px] flex flex-col">
          {history.length === 0 && !state.displayedResponse && (
            <div className="text-gray-500 text-sm text-center py-4 flex-1 flex items-center justify-center">Start a conversation...</div>
          )}
          {/* Messages - oldest first, newest at bottom */}
          {history.slice(-10).map((msg, i) => (
            <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] px-3 py-2 rounded-xl text-sm ${
                msg.role === 'user'
                  ? 'bg-blue-500/20 border border-blue-500/30 text-blue-300'
                  : 'bg-gray-700/50 border border-gray-600/50 text-gray-200'
              }`}>
                {msg.text.slice(0, 200)}{msg.text.length > 200 ? '...' : ''}
              </div>
            </div>
          ))}
          {/* Live transcript while listening */}
          {state.mode === 'listening' && state.transcript && (
            <div className="flex justify-end">
              <div className="bg-cyan-500/20 border border-cyan-500/30 rounded-xl px-3 py-2 max-w-[85%]">
                <p className="text-xs text-cyan-300">{state.transcript}<span className="animate-pulse">|</span></p>
              </div>
            </div>
          )}
          {/* Response with typewriter - shows at bottom during typing */}
          {state.displayedResponse && state.mode === 'speaking' && (
            <div className="flex justify-start">
              <div className="bg-purple-500/20 border border-purple-500/30 rounded-xl px-3 py-2 max-w-[85%]">
                <p className="text-sm text-gray-200">{state.displayedResponse}</p>
              </div>
            </div>
          )}
        </div>

        {/* Input Area */}
        <div className="p-3 bg-gradient-to-t from-[#0d1117] to-transparent border-t border-purple-500/20">
          <div className="flex items-center gap-2">
            {/* Voice Button */}
            <button
              onClick={state.mode === 'listening' ? stopListening : startListening}
              disabled={state.mode === 'thinking' || state.mode === 'speaking'}
              className={`relative p-2.5 rounded-full transition-all duration-300 ${
                state.mode === 'listening'
                  ? 'bg-gradient-to-br from-cyan-500 to-blue-600 text-white shadow-lg shadow-cyan-500/50'
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white'
              } disabled:opacity-50`}
            >
              {state.mode === 'listening' ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
              {state.mode === 'listening' && (
                <span className="absolute inset-0 rounded-full border-2 border-cyan-400 animate-ping" />
              )}
            </button>

            {/* Text Input */}
            <form onSubmit={handleTextSubmit} className="flex-1 flex gap-2">
              <input
                ref={textInputRef}
                type="text"
                value={textInput}
                onChange={(e) => setTextInput(e.target.value)}
                placeholder={taskPromptMode ? "Describe your task..." : "Type or speak..."}
                disabled={state.mode !== 'idle'}
                className={`flex-1 bg-gray-800/50 border rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none disabled:opacity-50 ${
                  taskPromptMode
                    ? 'border-purple-500 focus:border-purple-400 ring-1 ring-purple-500/30'
                    : 'border-gray-700 focus:border-purple-500'
                }`}
              />
              <button
                type="submit"
                disabled={!textInput.trim() || state.mode !== 'idle'}
                className={`p-2 rounded-lg text-white disabled:opacity-50 hover:shadow-lg transition-all ${
                  taskPromptMode
                    ? 'bg-gradient-to-r from-green-500 to-emerald-500 hover:shadow-green-500/30'
                    : 'bg-gradient-to-r from-purple-500 to-pink-500 hover:shadow-purple-500/30'
                }`}
              >
                <Send className="w-4 h-4" />
              </button>
            </form>

            {/* Mute Toggle */}
            <button
              onClick={() => setIsMuted(!isMuted)}
              className={`p-2 rounded-lg transition-all ${
                isMuted ? 'bg-red-500/20 text-red-400' : 'bg-gray-800 text-gray-400 hover:text-white'
              }`}
            >
              {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </button>
          </div>

          {/* Quick Actions */}
          <div className="flex gap-1.5 mt-2 flex-wrap">
            <button
              onClick={() => processInput('status')}
              disabled={state.mode !== 'idle'}
              className="px-2 py-1 text-[10px] rounded bg-gray-800/50 border border-gray-700 text-gray-400 hover:text-white hover:border-purple-500/50 transition-all disabled:opacity-50"
            >
              Status
            </button>
            <button
              onClick={() => {
                setTaskPromptMode(true);
                setTextInput('');
                setTimeout(() => textInputRef.current?.focus(), 50);
              }}
              disabled={state.mode !== 'idle'}
              className={`px-2 py-1 text-[10px] rounded border transition-all disabled:opacity-50 ${
                taskPromptMode
                  ? 'bg-purple-500/30 border-purple-500 text-purple-300'
                  : 'bg-gray-800/50 border-gray-700 text-gray-400 hover:text-white hover:border-purple-500/50'
              }`}
            >
              {taskPromptMode ? '📝 Enter task...' : 'Submit Task'}
            </button>
            <button
              onClick={() => processInput('deploy')}
              disabled={state.mode !== 'idle'}
              className="px-2 py-1 text-[10px] rounded bg-gray-800/50 border border-gray-700 text-gray-400 hover:text-white hover:border-purple-500/50 transition-all disabled:opacity-50"
            >
              Deploy
            </button>
            <button
              onClick={async () => {
                const msg = textInput.trim() || 'ping';
                setTextInput('');
                setState(prev => ({ ...prev, mode: 'thinking' }));
                setHistory(prev => [...prev, { role: 'user', text: `🤖 To Claude Code: ${msg}` }]);
                try {
                  const res = await fetch(`${SYNAPSE_API}/claude-code`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'X-API-Key': 'synapse-dev' },
                    body: JSON.stringify({ message: msg, from: 'brain' })
                  });
                  if (res.ok) {
                    const data = await res.json();
                    handleResponse(data.response || 'Message sent to Claude Code');
                  } else {
                    handleResponse('Claude Code endpoint not available');
                  }
                } catch (e) {
                  handleResponse('Failed to reach Claude Code');
                }
              }}
              disabled={state.mode !== 'idle'}
              className="px-2 py-1 text-[10px] rounded bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border border-cyan-500/50 text-cyan-400 hover:border-cyan-400 hover:text-cyan-300 transition-all disabled:opacity-50"
            >
              🤖 Ask Claude Code
            </button>
            {taskPromptMode && (
              <button
                onClick={() => setTaskPromptMode(false)}
                className="px-2 py-1 text-[10px] rounded bg-red-500/20 border border-red-500/50 text-red-400 hover:bg-red-500/30 transition-all"
              >
                Cancel
              </button>
            )}
          </div>
        </div>

        {/* CSS Keyframes */}
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          @keyframes float {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-4px); }
          }
        `}</style>
      </div>
    );
  }

  // FULL MODE: Centered command hub
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center">
      {/* Backdrop with blur */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Main Brain Interface */}
      <div className="relative w-[700px] max-w-[95vw] max-h-[90vh] flex flex-col">
        {/* Top controls: Popup mode + Mini mode + Close */}
        <div className="absolute -top-2 -right-2 z-10 flex gap-2">
          <button
            onClick={() => setPopupMode(true)}
            className="p-2 rounded-full bg-gray-800/80 border border-gray-700 text-gray-400 hover:text-white hover:bg-gray-700 transition-all"
            title="Switch to chat popup"
          >
            <Activity className="w-5 h-5" />
          </button>
          <button
            onClick={() => setMiniMode(true)}
            className="p-2 rounded-full bg-gray-800/80 border border-gray-700 text-gray-400 hover:text-white hover:bg-gray-700 transition-all"
            title="Minimize to orb"
          >
            <Minimize2 className="w-5 h-5" />
          </button>
          <button
            onClick={onClose}
            className="p-2 rounded-full bg-gray-800/80 border border-gray-700 text-gray-400 hover:text-white hover:bg-gray-700 transition-all"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* The Brain Core - Saucer with shimmer glow */}
        <div className="relative flex flex-col items-center pt-4">
          {/* Outer shimmer ring - rotating gradient */}
          <div
            className="absolute w-56 h-56 rounded-full pointer-events-none"
            style={{
              background: `conic-gradient(from 0deg, transparent 0%, rgba(139,92,246,0.6) 10%, rgba(236,72,153,0.6) 20%, rgba(34,211,238,0.6) 30%, transparent 40%, transparent 60%, rgba(34,211,238,0.6) 70%, rgba(236,72,153,0.6) 80%, rgba(139,92,246,0.6) 90%, transparent 100%)`,
              animation: 'spin 4s linear infinite',
              filter: 'blur(12px)'
            }}
          />
          {/* Inner shimmer ring - counter-rotate */}
          <div
            className="absolute w-44 h-44 rounded-full pointer-events-none"
            style={{
              background: `conic-gradient(from 180deg, transparent 0%, rgba(34,211,238,0.5) 15%, rgba(139,92,246,0.5) 30%, transparent 45%, transparent 55%, rgba(139,92,246,0.5) 70%, rgba(34,211,238,0.5) 85%, transparent 100%)`,
              animation: 'spin 3s linear infinite reverse',
              filter: 'blur(8px)'
            }}
          />

          {/* Saucer image with audio-reactive effects */}
          <div
            className="relative w-40 h-40 flex items-center justify-center transition-transform duration-100"
            style={{ transform: `scale(${1 + state.audioLevel * 0.15})` }}
          >
            {/* Beam effect underneath saucer */}
            <div
              className="absolute bottom-0 left-1/2 -translate-x-1/2 w-20 rounded-full pointer-events-none transition-all duration-200"
              style={{
                height: state.mode === 'listening' ? '60px' : state.mode === 'speaking' ? '40px' : '20px',
                background: `linear-gradient(to bottom, ${
                  state.mode === 'listening' ? 'rgba(34,211,238,0.6)' :
                  state.mode === 'speaking' ? 'rgba(34,197,94,0.6)' :
                  state.mode === 'thinking' ? 'rgba(168,85,247,0.6)' :
                  'rgba(100,100,100,0.3)'
                }, transparent)`,
                filter: 'blur(4px)',
                animation: state.mode !== 'idle' ? 'pulse 1.5s ease-in-out infinite' : 'none'
              }}
            />

            {/* The Saucer */}
            <img
              src="/assets/saucer.png"
              alt="Mothership"
              className="w-36 h-auto relative z-10 drop-shadow-2xl"
              style={{
                filter: `drop-shadow(0 0 20px ${modeStyles.glowColor}) drop-shadow(0 0 40px ${modeStyles.glowColor})`,
                animation: state.mode === 'thinking' ? 'float 2s ease-in-out infinite' : 'none'
              }}
            />

            {/* Mode indicator overlay */}
            {state.mode === 'thinking' && (
              <div className="absolute inset-0 flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-purple-400 animate-spin opacity-80" />
              </div>
            )}
          </div>

          {/* Status label */}
          <div className="mt-2 text-center">
            <div className={`text-sm font-medium transition-colors ${
              state.mode === 'listening' ? 'text-cyan-400' :
              state.mode === 'thinking' ? 'text-purple-400' :
              state.mode === 'speaking' ? 'text-green-400' :
              'text-gray-400'
            }`}>
              {state.mode === 'listening' ? 'Listening...' :
               state.mode === 'thinking' ? 'Processing...' :
               state.mode === 'speaking' ? 'Speaking...' :
               'Ready'}
            </div>
            <div className="text-xs text-gray-500 mt-0.5 tracking-widest">MOTHERSHIP AI</div>
          </div>
        </div>

        {/* Main Content Panel */}
        <div className="mt-6 bg-[#0d1117]/95 backdrop-blur-xl rounded-2xl border border-purple-500/30 shadow-2xl shadow-purple-500/20 overflow-hidden">
          {/* System Status Bar */}
          <div className="flex items-center justify-between px-4 py-2 bg-gradient-to-r from-purple-900/30 via-transparent to-cyan-900/30 border-b border-purple-500/20">
            <div className="flex items-center gap-4 text-xs">
              <div className={`flex items-center gap-1.5 ${state.systemStatus.synapse ? 'text-green-400' : 'text-red-400'}`}>
                <span className={`w-2 h-2 rounded-full ${state.systemStatus.synapse ? 'bg-green-400' : 'bg-red-400'} animate-pulse`} />
                Synapse
              </div>
              <div className="text-cyan-400 flex items-center gap-1">
                <Activity className="w-3 h-3" />
                {state.systemStatus.workers} active
              </div>
              <div className="text-purple-400 flex items-center gap-1">
                <Zap className="w-3 h-3" />
                {state.systemStatus.queue} queued
              </div>
              <div className="text-emerald-400 flex items-center gap-1">
                <Sparkles className="w-3 h-3" />
                {state.systemStatus.tasks_completed} done
              </div>
            </div>
            <div className="text-[10px] text-gray-500 font-mono">v2.0 BRAIN</div>
          </div>

          {/* Conversation Display */}
          <div className="h-48 overflow-y-auto p-4 space-y-3">
            {/* Live transcript */}
            {state.mode === 'listening' && state.transcript && (
              <div className="flex justify-end">
                <div className="bg-cyan-500/20 border border-cyan-500/30 rounded-xl px-4 py-2 max-w-[80%]">
                  <p className="text-sm text-cyan-300">{state.transcript}<span className="animate-pulse">|</span></p>
                </div>
              </div>
            )}

            {/* Response with typewriter */}
            {state.displayedResponse && (
              <div className="flex justify-start">
                <div className="bg-purple-500/20 border border-purple-500/30 rounded-xl px-4 py-2 max-w-[80%]">
                  <p className="text-sm text-gray-200">{state.displayedResponse}</p>
                </div>
              </div>
            )}

            {/* History (last few messages) */}
            {history.slice(-4).map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`rounded-xl px-4 py-2 max-w-[80%] ${
                  msg.role === 'user'
                    ? 'bg-blue-500/20 border border-blue-500/30'
                    : 'bg-gray-800/50 border border-gray-700/50'
                }`}>
                  <p className={`text-sm ${msg.role === 'user' ? 'text-blue-300' : 'text-gray-300'}`}>
                    {msg.text.slice(0, 200)}{msg.text.length > 200 ? '...' : ''}
                  </p>
                </div>
              </div>
            )).reverse()}
          </div>

          {/* Input Area */}
          <div className="p-4 bg-gradient-to-t from-[#0d1117] to-transparent border-t border-purple-500/20">
            <div className="flex items-center gap-3">
              {/* Voice Button */}
              <button
                onClick={state.mode === 'listening' ? stopListening : startListening}
                disabled={state.mode === 'thinking' || state.mode === 'speaking'}
                className={`relative p-4 rounded-full transition-all duration-300 ${
                  state.mode === 'listening'
                    ? 'bg-gradient-to-br from-cyan-500 to-blue-600 text-white shadow-lg shadow-cyan-500/50 scale-110'
                    : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {state.mode === 'listening' ? (
                  <MicOff className="w-6 h-6" />
                ) : (
                  <Mic className="w-6 h-6" />
                )}
                {state.mode === 'listening' && (
                  <span className="absolute inset-0 rounded-full border-2 border-cyan-400 animate-ping" />
                )}
              </button>

              {/* Text Input */}
              <form onSubmit={handleTextSubmit} className="flex-1 flex gap-2">
                <input
                  type="text"
                  value={textInput}
                  onChange={(e) => setTextInput(e.target.value)}
                  placeholder="Type a command or speak..."
                  disabled={state.mode !== 'idle'}
                  className="flex-1 bg-gray-800/50 border border-gray-700 rounded-xl px-4 py-3 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 disabled:opacity-50"
                />
                <button
                  type="submit"
                  disabled={!textInput.trim() || state.mode !== 'idle'}
                  className="p-3 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 text-white disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg hover:shadow-purple-500/30 transition-all"
                >
                  <Send className="w-5 h-5" />
                </button>
              </form>

              {/* Mute Toggle */}
              <button
                onClick={() => setIsMuted(!isMuted)}
                className={`p-3 rounded-xl transition-all ${
                  isMuted ? 'bg-red-500/20 text-red-400' : 'bg-gray-800 text-gray-400 hover:text-white'
                }`}
              >
                {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
              </button>
            </div>

            {/* Quick Actions */}
            <div className="flex gap-2 mt-3">
              {['Status', 'Submit Task', 'Deploy', 'Logs'].map(action => (
                <button
                  key={action}
                  onClick={() => processInput(action.toLowerCase())}
                  disabled={state.mode !== 'idle'}
                  className="px-3 py-1.5 text-xs rounded-lg bg-gray-800/50 border border-gray-700 text-gray-400 hover:text-white hover:border-purple-500/50 transition-all disabled:opacity-50"
                >
                  {action}
                </button>
              ))}
              {/* Checklist toggle */}
              <button
                onClick={() => setShowChecklist(!showChecklist)}
                className={`px-3 py-1.5 text-xs rounded-lg border transition-all ${
                  showChecklist
                    ? 'bg-purple-500/20 border-purple-500/50 text-purple-300'
                    : 'bg-gray-800/50 border-gray-700 text-gray-400 hover:text-white hover:border-purple-500/50'
                }`}
              >
                📋 Tasks {tasks.length > 0 && `(${tasks.filter(t => !t.done).length})`}
              </button>
            </div>
          </div>
        </div>

        {/* Floating Checklist Panel */}
        {showChecklist && (
          <div className="absolute -right-80 top-20 w-72 bg-[#0d1117]/95 backdrop-blur-xl rounded-xl border border-purple-500/30 shadow-2xl overflow-hidden">
            <div className="px-4 py-3 bg-gradient-to-r from-purple-900/30 to-cyan-900/30 border-b border-purple-500/20">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-200">📋 Task Checklist</span>
                <span className="text-xs text-gray-500">{tasks.filter(t => t.done).length}/{tasks.length}</span>
              </div>
            </div>

            {/* Task list */}
            <div className="max-h-64 overflow-y-auto p-2 space-y-1">
              {tasks.length === 0 ? (
                <div className="text-center py-4 text-gray-500 text-xs">No tasks yet</div>
              ) : tasks.map(task => (
                <div
                  key={task.id}
                  className={`flex items-center gap-2 px-2 py-1.5 rounded-lg group ${
                    task.done ? 'bg-green-500/10' : 'bg-gray-800/30'
                  }`}
                >
                  <button
                    onClick={() => toggleTask(task.id)}
                    className={`w-5 h-5 rounded border flex-shrink-0 flex items-center justify-center transition-all ${
                      task.done
                        ? 'bg-green-500 border-green-500 text-white'
                        : 'border-gray-600 hover:border-purple-500'
                    }`}
                  >
                    {task.done && '✓'}
                  </button>
                  <span className={`flex-1 text-xs ${task.done ? 'text-gray-500 line-through' : 'text-gray-300'}`}>
                    {task.text}
                  </span>
                  <button
                    onClick={() => deleteTask(task.id)}
                    className="opacity-0 group-hover:opacity-100 text-gray-500 hover:text-red-400 text-xs transition-opacity"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>

            {/* Add task input */}
            <div className="p-2 border-t border-gray-800">
              <form
                onSubmit={(e) => { e.preventDefault(); addTask(newTaskText); }}
                className="flex gap-2"
              >
                <input
                  type="text"
                  value={newTaskText}
                  onChange={(e) => setNewTaskText(e.target.value)}
                  placeholder="Add a task..."
                  className="flex-1 bg-gray-800/50 border border-gray-700 rounded-lg px-3 py-1.5 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                />
                <button
                  type="submit"
                  disabled={!newTaskText.trim()}
                  className="px-3 py-1.5 text-xs rounded-lg bg-purple-500/20 text-purple-300 hover:bg-purple-500/30 disabled:opacity-50 transition-all"
                >
                  Add
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Footer hint */}
        <div className="text-center mt-4 text-xs text-gray-500">
          Press <kbd className="px-1.5 py-0.5 bg-gray-800 rounded border border-gray-700">Esc</kbd> to close
          • Voice commands: "Status", "Submit task [description]", "Deploy"
        </div>
      </div>

      {/* CSS Keyframes */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 1; }
        }
      `}</style>
    </div>
  );
}
